/**
 * CyberSells Core - Vanilla JS class-based module aggregation system
 * Safe for Dawn-like Shopify themes with idempotent initialization
 */

class CyberSells {
  constructor(config = {}) {
    this.config = {
      statsMode: config.statsMode || 'mock',
      statsEndpoint: config.statsEndpoint || '/apps/cybersells-stats',
      debug: config.debug || false,
      ...config
    };
    
    this.initialized = false;
    this.modules = {};
    this._toastSystem = null;
    this._loggedErrors = new Set();
  }
  
  /**
   * Debug logging helper - only logs when debug flag is enabled
   */
  _debug(...args) {
    if (this.config.debug) {
      console.log('[CyberSells]', ...args);
    }
  }

  /**
   * Creates the context object passed to all module setup functions
   * @returns {Object} Context object with shared utilities and config
   */
  _createContext() {
    return {
      config: this.config,
      root: document,
      storage: window.localStorage,
      now: Date.now,  // Function reference - modules should call ctx.now() to get current time
      sessionStorage: window.sessionStorage,
      console: window.console
    };
  }

  /**
   * Safely imports and executes a module setup function
   * Gracefully handles missing modules or errors
   * @param {string} moduleName - Name of the module for logging
   * @param {Function} setupFn - Module setup function to execute
   * @param {Object} ctx - Context object
   * @returns {*} Result of setup function or null on error
   */
  _safeModuleSetup(moduleName, setupFn, ctx) {
    try {
      if (typeof setupFn !== 'function') {
        throw new Error(`${moduleName} setup function is not available`);
      }
      return setupFn(ctx);
    } catch (error) {
      // Log once per module to avoid spam
      const errorKey = `${moduleName}:${error.message}`;
      if (!this._loggedErrors.has(errorKey)) {
        console.warn(`[CyberSells] Module "${moduleName}" error:`, error.message);
        this._loggedErrors.add(errorKey);
      }
      return null;
    }
  }

  /**
   * Idempotent initialization - safe to call multiple times
   * Initializes all core modules with graceful error handling
   */
  init() {
    // Guard against double initialization
    if (this.initialized) {
      this._debug('Already initialized, skipping...');
      return this;
    }

    this._debug('Initializing...');
    
    const ctx = this._createContext();

    // Attempt to import modules from index.js
    let modules = {};
    try {
      // Modules are loaded individually and export to window.CyberSells_*
      // This aggregation is optional - modules work standalone
      if (window.CyberSellsModules) {
        modules = window.CyberSellsModules;
      }
    } catch (error) {
      console.warn('[CyberSells] Could not load modules:', error.message);
    }

    // NOTE: Module initialization is handled by cybersells-core.js
    // Individual modules export to window.CyberSells_* and are initialized there
    // This class provides shared utilities and config, not module orchestration

    // Store module references for wrapper methods
    this.modules = modules;

    this.initialized = true;
    this._debug('Initialization complete');
    
    return this;
  }

  /**
   * Fetches player statistics - wrapper for stats module
   * @param {Object} params - Parameters for stats fetching
   * @returns {Promise<Object>} Player stats data
   */
  async fetchPlayerStats(params = {}) {
    const ctx = this._createContext();
    
    if (!this.modules.fetchPlayerStats) {
      console.warn('[CyberSells] fetchPlayerStats module not available');
      // Return mock data as fallback
      return {
        success: false,
        error: 'Stats module not available',
        data: null
      };
    }

    try {
      const result = await this.modules.fetchPlayerStats(ctx, params);
      // Handle null/undefined returns from stub functions
      if (result === null || result === undefined) {
        return {
          success: false,
          error: 'Stats module not implemented',
          data: null
        };
      }
      return result;
    } catch (error) {
      console.error('[CyberSells] Error fetching player stats:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  /**
   * Displays an achievement toast - wrapper for toast module
   * @param {Object} achievement - Achievement data to display
   * @param {string} achievement.title - Achievement title
   * @param {string} achievement.description - Achievement description
   * @param {string} achievement.icon - Achievement icon (optional)
   */
  showAchievement(achievement) {
    if (!this._toastSystem || typeof this._toastSystem.showAchievement !== 'function') {
      console.warn('[CyberSells] Toast system not available, cannot show achievement');
      return;
    }

    try {
      this._toastSystem.showAchievement(achievement);
    } catch (error) {
      console.error('[CyberSells] Error showing achievement:', error);
    }
  }

  /**
   * Show level up notification
   * @param {Object} data - Level up data
   */
  showLevelUp(data) {
    if (!this._toastSystem || typeof this._toastSystem.showLevelUp !== 'function') {
      console.warn('[CyberSells] Toast system not available, cannot show level up');
      return;
    }

    try {
      this._toastSystem.showLevelUp(data);
    } catch (error) {
      console.error('[CyberSells] Error showing level up:', error);
    }
  }

  /**
   * Cleanup method (if needed in the future)
   */
  destroy() {
    this.initialized = false;
    this._toastSystem = null;
    this.modules = {};
    this._loggedErrors.clear();
    this._debug('Destroyed');
  }
}

// Expose CyberSells class to window
if (typeof window !== 'undefined') {
  window.CyberSells = CyberSells;
}

